/*
 * fm-mobile-collection.js — mobile Filter & Sort bar behaviour.
 * Mobile collection redesign, 28-Jul-2026.
 *
 * Deliberately thin. Two jobs:
 *
 *  1. Filter — forward the bar's Filter button to the existing
 *     `<menu-drawer>` summary. Dawn's MenuDrawer binds a plain click listener
 *     to that summary (global.js `bindEvents`), so a synthetic click gets the
 *     real open animation, focus trap and Escape handling for free. Nothing
 *     about the drawer's own markup or JS is duplicated here.
 *
 *  2. Sort — the sheet writes `sort_by` into the URL and navigates, rather
 *     than posting through facets.js. That is intentional: the brief requires
 *     sort to stay a shareable, back-navigable URL param, and facets.js's
 *     `onSubmitHandler` drops non-mobile form values whenever the submitting
 *     form is `#FacetFiltersFormMobile`, so a sort control living outside that
 *     form cannot round-trip reliably through the AJAX path. A URL change is
 *     both correct and immune to that.
 *
 * Every existing filter param survives because we mutate the current
 * URLSearchParams rather than building a fresh query.
 */
(function () {
  'use strict';

  var sheet = document.getElementById('FmSortSheet');
  var sortOpen = document.getElementById('FmSortOpen');
  var filterOpen = document.getElementById('FmFilterOpen');
  var bar = document.querySelector('.fm-mfs');

  /*
   * Portal the sheet to <body>.
   *
   * The sheet is authored inside the facets markup, which sits under
   * `.collection__filter-wrapper` — and that carries `position: relative;
   * z-index: 5`, making it a stacking context. Anything inside it is capped at
   * level 5 no matter its own z-index, so the scrim could never cover the site
   * header (`position: fixed`, z 50): raising the scrim to 100 changed nothing.
   * Re-parenting to <body> puts the sheet in the root stacking context, where
   * its z-index actually competes with the header's.
   */
  if (sheet && sheet.parentElement !== document.body) {
    document.body.appendChild(sheet);
  }

  /*
   * The bar is sticky beneath the site header, so it needs the header's real
   * height. That is not a constant: the header is `position: fixed` and its
   * height varies with the announcement bar and between breakpoints, and the
   * design's nominal 54px does not match what the theme actually renders (76px
   * at the time of writing). Measure it instead of hardcoding, and re-measure
   * on resize/orientation change. The CSS carries a sane fallback.
   */
  function syncHeaderOffset() {
    if (!bar) return;
    var header = document.querySelector('.eq-header__top');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) bar.style.setProperty('--fm-header-h', h + 'px');
  }

  syncHeaderOffset();
  window.addEventListener('resize', syncHeaderOffset);
  window.addEventListener('orientationchange', syncHeaderOffset);

  function openSheet() {
    if (!sheet) return;
    sheet.hidden = false;
    if (sortOpen) sortOpen.setAttribute('aria-expanded', 'true');
    // Stop the page behind the sheet from scrolling under the finger.
    document.body.classList.add('fm-sheet-open');
  }

  function closeSheet() {
    if (!sheet) return;
    sheet.hidden = true;
    if (sortOpen) sortOpen.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('fm-sheet-open');
  }

  if (sortOpen) {
    sortOpen.addEventListener('click', function () {
      if (sheet && sheet.hidden) openSheet();
      else closeSheet();
    });
  }

  if (filterOpen) {
    filterOpen.addEventListener('click', function () {
      // One sheet at a time — the brief makes the three sheets mutually
      // exclusive, so opening the filter drawer dismisses the sort sheet.
      closeSheet();
      var summary = document.querySelector('.mobile-facets__wrapper summary.mobile-facets__open-wrapper');
      if (summary) summary.click();
      // After the drawer is shown, so the attributes stick.
      setTimeout(function () {
        fmApplyDefaultGroupState();
        fmDecorateFacetGroups();
      }, 260);
    });
  }

  if (sheet) {
    sheet.addEventListener('click', function (event) {
      // `closest`, not the target itself: the close control is a <button>, and a
      // tap can land on a child node (or a glyph's text node's element), which
      // would miss an exact-target check and leave the sheet stuck open.
      if (event.target.closest('[data-fm-sheet-close]')) {
        closeSheet();
        return;
      }

      var row = event.target.closest('[data-fm-sort-value]');
      if (!row) return;

      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', row.getAttribute('data-fm-sort-value'));
      // A sort change invalidates the current page offset.
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && sheet && !sheet.hidden) closeSheet();
  });

  /*
   * Help bar Chat button -> the real chat launcher.
   *
   * The launcher is a third-party widget hidden on mobile by CSS, because it
   * floated over product cards (brief finding 2.2). Forwarding a click keeps
   * the actual support channel working instead of reimplementing it, and
   * `display: none` does not stop a programmatic click.
   *
   * Prefix match, not the full class: the app ships a hashed suffix
   * (HubLauncher-floatingWidget-XXXXX) that changes between releases. Queried
   * at click time rather than on load, since the widget injects late.
   */
  /*
   * Fold the buying-guide content into the accordion (mobile only).
   *
   * The design's buying-guide card is not new content — it is the existing
   * fm-image-with-text section (in-situ photo + benefit grid) and
   * fm-how-to-choose section (numbered steps with filter shortcut chips)
   * consolidated inside "Which entrance mat do I need?".
   *
   * These are MOVED, not copied. Rebuilding them inside the accordion would put
   * the same body copy on the page twice, which is worse for search than the
   * problem it solves, and would leave two sources to keep in step. Moving keeps
   * one copy, keeps the theme editor as the place the content is edited, and
   * keeps any listeners already bound to those nodes.
   *
   * Mobile only, decided once at load: real devices do not cross the 767px
   * boundary mid-session, and re-parenting on every resize would be a lot of
   * churn for a case that does not happen. On desktop the sections stay exactly
   * where they are, which is what the brief requires.
   */
  var isMobile = window.matchMedia('(max-width: 767px)').matches;
  var guideBody = document.querySelector('.fm-coll-guide__body');
  if (isMobile && guideBody) {
    ['.fm-iwt', '.fm-htc'].forEach(function (sel) {
      var inner = document.querySelector(sel);
      var section = inner && inner.closest('[id^="shopify-section"]');
      if (section && section.parentElement !== guideBody) {
        section.classList.add('fm-coll-guide__absorbed');
        guideBody.appendChild(section);
      }
    });

    /*
     * The explainer video renders inside the guide body server-side (AF
     * redesign, 29-Jul-2026) and the brief puts it at the BOTTOM of the
     * accordion — after the absorbed sections, which appendChild has just
     * placed after it. One more move keeps the order: text, absorbed
     * content, video.
     */
    var guideVideo = document.getElementById('FmGuideVideo');
    if (guideVideo && guideVideo.parentElement === guideBody) {
      guideBody.appendChild(guideVideo);
    }
  }

  /*
   * The breadcrumb move that used to live here is gone (06-Aug-2026).
   *
   * It relocated the crumb <nav> into the hero panel so the crumb would inherit
   * the panel's padding and align with the H1. That worked, but this script is
   * `defer`, so the move happened after first paint: the panel gained 26px, the
   * crumb jumped 12px, and the grid below restructured by 112px. Measured, it
   * was the single largest layout shift on the collection page.
   *
   * fm-collection-hero.liquid now renders the crumb in both positions and CSS
   * shows one per breakpoint, so the final layout is the server's first paint.
   * The `fm-coll-hero__breadcrumbs--in-panel` class this code used to add at
   * runtime is now in the markup; it never had a CSS rule of its own before, so
   * nothing depended on the JS having applied it.
   */


  /*
   * Put the trust strip ABOVE the buying guide.
   *
   * The guide is rendered inside the product-grid section so it sits directly
   * under the grid, which puts it before the trust strip section in document
   * order. The design has the trust strip first, as a quiet band between the
   * products and the guide.
   *
   * Moving the guide after the trust strip is the smaller change: the strip is a
   * top-level section a director may reorder in the theme editor, whereas the
   * guide's position is only meaningful relative to the grid it belongs to.
   * Runs after the absorb step above, so the guide is already complete when it
   * moves.
   */
  if (isMobile) {
    var guide = document.querySelector('.fm-coll-guide');
    var proofInner = document.querySelector('.fm-proof-strip');
    var proofSection = proofInner && proofInner.closest('[id^="shopify-section"]');
    if (guide && proofSection && proofSection.parentElement) {
      proofSection.parentElement.insertBefore(guide, proofSection.nextSibling);
    }
  }

  /*
   * Give the filter sheet a working way out.
   *
   * The theme's close control sits inside the drawer's <summary>, and the bar's
   * Filter button replaced that summary by hiding it — which hid the only exit
   * with it. The sheet could be opened and not dismissed. That is a trap, not a
   * cosmetic gap, so the sheet gets its own ✕ and a tappable scrim.
   *
   * Both close by clicking the summary rather than stripping the [open]
   * attribute, so Dawn's own open/close bookkeeping — body scroll lock, focus
   * trap, aria state — runs exactly as it does for its own control. Setting
   * attributes directly would leave the page scroll-locked with no drawer.
   */
  var facetDisclosure = document.querySelector('.mobile-facets__wrapper .mobile-facets__disclosure');
  if (facetDisclosure) {
    var facetSummary = facetDisclosure.querySelector('summary');

    var closeFacets = function () {
      if (facetDisclosure.hasAttribute('open') && facetSummary) facetSummary.click();
    };

    // ✕ in the sheet header.
    var facetHeader = facetDisclosure.querySelector('.mobile-facets__header');
    if (facetHeader && !facetHeader.querySelector('.fm-facet-close')) {
      var xBtn = document.createElement('button');
      xBtn.type = 'button';
      xBtn.className = 'fm-facet-close';
      xBtn.setAttribute('aria-label', 'Close filters');
      xBtn.innerHTML = '&times;';
      xBtn.addEventListener('click', closeFacets);
      facetHeader.appendChild(xBtn);
    }

    // Real scrim element, so tapping outside dismisses. A CSS pseudo-element
    // cannot take a tap, which is why the earlier scrim looked right but did
    // nothing.
    if (!document.getElementById('FmFacetScrim')) {
      var scrim = document.createElement('div');
      scrim.id = 'FmFacetScrim';
      scrim.className = 'fm-facet-scrim';
      scrim.hidden = true;
      scrim.addEventListener('click', closeFacets);
      document.body.appendChild(scrim);

      /* Mirror the drawer's state onto the scrim, and onto <body>.
       *
       * The body class exists because the filter drawer is the one sheet that
       * signals itself with an `open` attribute rather than `fm-sheet-open`,
       * so nothing outside this file could tell it was up. The compare bar
       * needs to know: it sits at z 2000 to clear header chrome, which puts it
       * over the drawer's scrim, and a still-tappable Compare button there
       * would open a second sheet on top of the first — against the brief's
       * "one sheet at a time". */
      var syncFacetState = function () {
        var open = facetDisclosure.hasAttribute('open');
        scrim.hidden = !open;
        document.body.classList.toggle('fm-facets-open', open);
      };
      syncFacetState();
      new MutationObserver(syncFacetState)
        .observe(facetDisclosure, { attributes: true, attributeFilter: ['open'] });
    }

    // Escape as a third way out, for keyboard and for anyone whose tap misses.
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeFacets();
    });
  }

  /*
   * Pin the filter sheet's Reset/Apply footer.
   *
   * The designed sheet has a sticky footer, but the theme renders that footer
   * INSIDE `.mobile-facets__main` — the scrolling group list — rather than as a
   * sibling of it, so it scrolls away with the filters and no amount of CSS can
   * pin it where it sits. Moving it up one level makes it a flex sibling of the
   * scroll area, which is what the sheet's column layout expects.
   *
   * Guarded so it runs once and only for the sheet's own footer, not the nested
   * submenu footers that share the class.
   */
  var facetInner = document.querySelector('.mobile-facets__wrapper .mobile-facets__inner');
  if (facetInner) {
    var main = facetInner.querySelector('.mobile-facets__main');
    var sheetFooter = main && main.querySelector(':scope > .mobile-facets__footer, :scope > form > .mobile-facets__footer');
    if (!sheetFooter && main) {
      // Fall back to the last footer inside the list, which is the sheet's own.
      var all = main.querySelectorAll('.mobile-facets__footer');
      sheetFooter = all.length ? all[all.length - 1] : null;
    }
    if (sheetFooter && sheetFooter.parentElement !== facetInner) {
      facetInner.appendChild(sheetFooter);
    }
  }

  /*
   * Filter sheet's Apply button reads "Show {n} results".
   *
   * The design puts the live result count on the button so the effect of a
   * selection is visible before dismissing the sheet. The count is already
   * rendered server-side into #ProductCount and re-rendered by facets.js on
   * every filter change, so it is read from there rather than recounted —
   * one source of truth, and it stays correct for filters applied via URL.
   *
   * A MutationObserver rather than a facets.js hook: the theme does not expose
   * an event for "filters re-rendered", and observing the node that actually
   * changes is robust to how the re-render happens.
   */
  function syncApplyLabel() {
    var applyBtn = document.querySelector('.mobile-facets__footer .button--primary');
    var countEl = document.getElementById('ProductCount');
    if (!applyBtn || !countEl) return;
    var match = (countEl.textContent || '').match(/\d[\d,]*/);
    if (!match) return;
    var n = match[0];
    applyBtn.textContent = 'Show ' + n + ' result' + (n === '1' ? '' : 's');
  }

  var countNode = document.getElementById('ProductCount');
  if (countNode) {
    syncApplyLabel();
    new MutationObserver(syncApplyLabel).observe(countNode, {
      childList: true, characterData: true, subtree: true
    });
  }

  /*
   * Filter sheet: "Show all {n}" per group, and a selected-count badge.
   *
   * The design collapses any group with more than six options behind a "Show all
   * {n}" link, and puts a count badge in each group header when that group has
   * selections. Neither exists in the theme.
   *
   * Applied in JS rather than Liquid because facets.js REPLACES the drawer's
   * markup wholesale whenever a filter changes — anything rendered server-side
   * into these groups survives, but the collapsed state and the badge have to be
   * re-derived after each re-render. Hence the observer at the bottom.
   */
  var FM_VISIBLE = 6;
  var fmInitialGroupStateApplied = false;

  function fmDecorateFacetGroups() {
    var groups = document.querySelectorAll(
      '.mobile-facets__wrapper .mobile-facets__details, .mobile-facets__wrapper .facets__disclosure-vertical'
    );

    groups.forEach(function (group) {
      var list = group.querySelector('ul');
      var summary = group.querySelector('summary');
      if (!list || !summary) return;

      var items = list.querySelectorAll('li');

      /* ---- Show all {n} ---- */
      if (items.length > FM_VISIBLE && !group.querySelector('.fm-facet-more')) {
        items.forEach(function (li, i) {
          if (i >= FM_VISIBLE) li.classList.add('fm-facet-hidden');
        });

        var more = document.createElement('button');
        more.type = 'button';
        more.className = 'fm-facet-more';
        more.textContent = 'Show all ' + items.length;
        more.setAttribute('aria-expanded', 'false');
        more.addEventListener('click', function () {
          var expanded = group.classList.toggle('fm-facet-expanded');
          more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          more.textContent = expanded ? 'Show fewer' : 'Show all ' + items.length;
        });
        list.parentNode.insertBefore(more, list.nextSibling);
      }

      /* ---- Selected-count badge in the header ---- */
      var checked = list.querySelectorAll('input[type="checkbox"]:checked').length;
      var badge = summary.querySelector('.fm-facet-badge');

      if (checked > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'fm-facet-badge';
          /* Before the chevron, which is drawn on the summary itself. */
          summary.appendChild(badge);
        }
        badge.textContent = checked;
      } else if (badge) {
        badge.remove();
      }
    });

    /*
     * Only Colour open on load, everything else collapsed — the design's default.
     *
     * Applied ONCE, guarded by a module flag. fmDecorateFacetGroups also runs
     * after every facets.js re-render, and re-asserting the default there would
     * slam shut whatever group the shopper had just opened to make their
     * selection. The flag is module-level rather than a data attribute on the
     * group, because a re-render replaces those elements and would lose it.
     */
  }

  /*
   * Only Colour open, everything else collapsed — the design's default.
   *
   * Applied on the FIRST open of the sheet, not at script load: at load the
   * attributes get reset before the drawer is ever shown, so setting them then
   * had no effect. Guarded by a module flag so re-opening the sheet, or a
   * facets.js re-render, never slams shut a group the shopper just opened.
   */
  function fmApplyDefaultGroupState() {
    if (fmInitialGroupStateApplied) return;
    var groups = document.querySelectorAll(
      '.mobile-facets__wrapper .mobile-facets__details, .mobile-facets__wrapper .facets__disclosure-vertical'
    );
    if (!groups.length) return;
    fmInitialGroupStateApplied = true;
    /*
     * Toggle through each group's own <summary>, not by writing the `open`
     * attribute. Setting the attribute directly did not stick — the theme
     * manages this state and reset it before the sheet was shown. Clicking the
     * summary goes through the same path a shopper's tap does, which is the same
     * lesson the drawer's close button taught.
     */
    groups.forEach(function (group) {
      var wantOpen = group.classList.contains('fc-colour');
      var isOpen = group.hasAttribute('open');
      if (wantOpen === isOpen) return;
      var s = group.querySelector('summary');
      if (s) s.click();
    });
  }

  fmDecorateFacetGroups();

  /* facets.js swaps the drawer's HTML on every filter change, which drops the
     buttons and badges. Re-derive them when that happens, debounced so a burst of
     mutations only costs one pass. */
  var facetForm = document.querySelector('.mobile-facets__wrapper');
  if (facetForm) {
    var pending;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        fmDecorateFacetGroups();
        fmAttachFacetDrag();
      }, 120);
    }).observe(facetForm, { childList: true, subtree: true });
  }

  /*
   * Help bar reveals on scroll, not at the top of the page.
   *
   * Pinned from page load it ate 56px of the first screen — the very space the
   * hero rework was freeing up to get the first product row visible. So it starts
   * off-canvas and slides up once the product grid is genuinely in view, then
   * stays up for the rest of the page.
   *
   * Threshold is "the grid is 200px into the viewport" rather than a fixed scroll
   * distance, because the grid's offset moves with the hero, the Shop by Type row
   * and whether a filter is applied. Measured against the grid, it stays correct
   * when any of those change.
   *
   * rAF-throttled: scroll fires far more often than paint, and reading
   * getBoundingClientRect on every event forces layout each time.
   */
  var helpBar = document.getElementById('FmHelpBar');
  if (helpBar) {
    var grid = document.getElementById('product-grid') ||
               document.querySelector('#ProductGridContainer');
    var ticking = false;

    var syncHelpBar = function () {
      ticking = false;
      if (!grid) { helpBar.classList.add('is-revealed'); return; }
      var gridTop = grid.getBoundingClientRect().top + window.scrollY;
      var reveal = (window.scrollY + window.innerHeight) > (gridTop + 200);
      helpBar.classList.toggle('is-revealed', reveal);
    };

    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(syncHelpBar);
    };

    syncHelpBar();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /*
   * Drag-to-dismiss for both bottom sheets.
   *
   * The grabber implied a pull-down gesture and did nothing — an affordance that
   * lies is worse than no affordance. This makes it real for the sort sheet and
   * the filter sheet.
   *
   * The drag only starts in the top ~72px of the panel (the grabber/header zone).
   * Starting it anywhere would fight the scrolling filter list: a downward swipe
   * over the options must scroll them, not drag the sheet.
   *
   * Movement is applied through a CSS custom property plus a `.fm-dragging`
   * class, not an inline transform, because the filter sheet's base rule is
   * `transform: none !important` (needed to defeat Dawn's off-canvas translate)
   * and an inline style cannot beat `!important`.
   */
  function fmAttachDragToDismiss(panel, onDismiss) {
    if (!panel || panel.dataset.fmDrag === '1') return;
    panel.dataset.fmDrag = '1';

    var startY = null;
    var delta = 0;
    var dragging = false;
    var DISMISS_AT = 80;   // px pulled before it closes
    var GRAB_ZONE = 72;    // px from the panel top where a drag may begin

    panel.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      var touch = event.touches[0];
      if (touch.clientY - panel.getBoundingClientRect().top > GRAB_ZONE) return;
      startY = touch.clientY;
      delta = 0;
      dragging = true;
      panel.classList.add('fm-dragging');
    }, { passive: true });

    panel.addEventListener('touchmove', function (event) {
      if (!dragging) return;
      delta = event.touches[0].clientY - startY;
      // Downward only — an upward pull should do nothing, not lift the sheet.
      panel.style.setProperty('--fm-drag', (delta > 0 ? delta : 0) + 'px');
    }, { passive: true });

    var end = function () {
      if (!dragging) return;
      dragging = false;
      panel.classList.remove('fm-dragging');
      panel.style.removeProperty('--fm-drag');
      if (delta > DISMISS_AT) onDismiss();
      delta = 0;
    };

    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', end);
  }

  /* Shared with fm-compare.js, whose compare sheet is the third user of the
   * bottom-sheet shell and needs the same gesture. Exported rather than copied
   * so the dismiss threshold and grab zone stay in one place.
   *
   * Load order makes this safe: `product-grid` precedes `fm_compare_bar` in
   * templates/collection.json and both scripts are `defer`, so this file always
   * runs first. fm-compare.js still guards on its presence, and the sheet keeps
   * its ✕, scrim and Escape either way. */
  window.fmAttachSheetDrag = fmAttachDragToDismiss;

  // Sort sheet.
  if (sheet) fmAttachDragToDismiss(sheet.querySelector('.fm-sortsheet__sheet'), closeSheet);

  // Filter sheet — re-attached after facets.js re-renders the drawer, guarded by
  // the dataset flag so repeat calls are cheap.
  function fmAttachFacetDrag() {
    var disclosure = document.querySelector('.mobile-facets__wrapper .mobile-facets__disclosure');
    var inner = disclosure && disclosure.querySelector('.mobile-facets__inner');
    var summary = disclosure && disclosure.querySelector('summary');
    if (!inner || !summary) return;
    fmAttachDragToDismiss(inner, function () {
      if (disclosure.hasAttribute('open')) summary.click();
    });
  }
  fmAttachFacetDrag();

  var chatBtn = document.getElementById('FmHelpChat');
  if (chatBtn) {
    chatBtn.addEventListener('click', function () {
      var launcher = document.querySelector('[class*="HubLauncher-floatingWidget"]');
      if (!launcher) return;
      // The clickable node is sometimes a child button rather than the wrapper.
      var target = launcher.querySelector('button, a, [role="button"]') || launcher;
      target.click();
    });
  }
})();
